import { API_REQUEST } from '../../core/actions';
import * as AssetsAPI from '../../core/api/assets';

export const FETCH_ASSET_TEMPLATES = 'FETCH_ASSET_TEMPLATES';
export const FETCH_ASSET_GROUPS = 'FETCH_ASSET_GROUPS';
export const FETCH_ASSET_TEMPLATE_OCCURRENCES = 'FETCH_ASSET_TEMPLATE_OCCURRENCES';
export const DEPLOY_ASSET_TEMPLATE = 'DEPLOY_ASSET_TEMPLATE';
export const REMOVE_ASSET_TEMPLATE = 'REMOVE_ASSET_TEMPLATE';
export const GET_ASSET_TEMPLATE_OCCURRENCE = 'GET_ASSET_TEMPLATE_OCCURRENCE';
export const UPDATE_ASSET_TEMPLATE_OCCURRENCE = 'UPDATE_ASSET_TEMPLATE_OCCURRENCE';
export const DESELECT_TEMPLATE_OCCURRENCE = 'DESELECT_TEMPLATE_OCCURRENCE';
export const FETCH_ASSET_ITEMS = 'FETCH_ASSET_ITEMS';
export const FETCH_TASK_OCCURRENCES = 'FETCH_TASK_OCCURRENCES';
export const FETCH_ASSET_SITE_INVENTORIES = 'FETCH_ASSET_SITE_INVENTORIES';
export const GET_ASSET_TEMPLATE = 'GET_ASSET_TEMPLATE';
export const GET_ASSET_GROUP_DETAIL = 'GET_ASSET_GROUP_DETAIL';
export const GET_ASSET_ITEM_DETAIL = 'GET_ASSET_ITEM_DETAIL';
export const DESELECT_TEMPLATE = 'DESELECT_TEMPLATE';
export const RESET_ASSET_VIEW = 'RESET_ASSET_VIEW';
export const SELECT_SIDEBAR_ITEM = 'SELECT_SIDEBAR_ITEM';
export const DESELECT_SIDEBAR_ITEM = 'DESELECT_SIDEBAR_ITEM';
export const SELECT_ASSET_NODE = 'SELECT_ASSET_NODE';
export const TOGGLE_TREE_NODE = 'TOGGLE_TREE_NODE';
export const SET_ASSET_SEARCH_TERM = 'SET_ASSET_SEARCH_TERM';
export const EXPAND_ALL_NODES = 'EXPAND_ALL_NODES';
export const COLLAPSE_ALL_NODES = 'COLLAPSE_ALL_NODES';
export const OPEN_ADD_TEMPLATE_DRAWER = 'OPEN_ADD_TEMPLATE_DRAWER';
export const CLOSE_ADD_TEMPLATE_DRAWER = 'CLOSE_ADD_TEMPLATE_DRAWER';

export const fetchAssetTemplates = () => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getAssetTemplates,
  data: { query: { pageSize: 1000 } },
  action: FETCH_ASSET_TEMPLATES,
});

export const fetchAssetTemplateOccurrences = () => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getAssetTemplateOccurrences,
  data: { query: { pageSize: 1000 } },
  action: FETCH_ASSET_TEMPLATE_OCCURRENCES,
});

export const deployAssetTemplates = (assetTemplateIds, onDone) => {
  let pending = assetTemplateIds.length;
  const failedIds = [];
  const settle = () => {
    pending -= 1;
    if (pending === 0 && onDone) onDone(failedIds);
  };
  return assetTemplateIds.map((assetTemplateId) => ({
    type: API_REQUEST,
    callAPI: AssetsAPI.postAssetTemplateOccurrence,
    data: { assetTemplateId },
    action: DEPLOY_ASSET_TEMPLATE,
    voidCallback: settle,
    errorCallback: () => {
      failedIds.push(assetTemplateId);
      settle();
    },
    // e.g. too many extra sites selected: never sent, so it stays in the cart
    cancelCallback: () => {
      failedIds.push(assetTemplateId);
      settle();
    },
  }));
};

// Opens the detail view on what the site actually runs, rather than on the
// blueprint the deployment came from.
export const fetchAssetTemplateOccurrence = (id) => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getAssetTemplateOccurrence,
  data: { id },
  action: GET_ASSET_TEMPLATE_OCCURRENCE,
});

export const deselectTemplateOccurrence = () => ({ type: DESELECT_TEMPLATE_OCCURRENCE });

export const updateAssetTemplateOccurrence = (id, patch, onDone) => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.patchAssetTemplateOccurrence,
  data: { id, ...patch },
  action: UPDATE_ASSET_TEMPLATE_OCCURRENCE,
  voidCallback: onDone,
});

export const removeAssetTemplateFromSite = (occurrenceId, onDone) => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.archiveAssetTemplateOccurrence,
  data: { id: occurrenceId },
  action: REMOVE_ASSET_TEMPLATE,
  voidCallback: onDone,
});

export const fetchAssetGroups = () => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getAssetGroups,
  data: { query: { pageSize: 1000 } },
  action: FETCH_ASSET_GROUPS,
});

export const fetchAssetItems = () => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getAssetItems,
  data: { query: { pageSize: 1000 } },
  action: FETCH_ASSET_ITEMS,
});

// Feeds the "Link existing task" picker on the asset detail drawer.
export const fetchAssetTaskOccurrences = () => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getTaskOccurrences,
  data: { query: { pageSize: 1000 } },
  action: FETCH_TASK_OCCURRENCES,
});

// Pulls the full site inventory. Feeds the "Link existing inventory"
// flow on the asset detail drawer; items with a null assetItemId are
// candidates to be linked to an asset.
export const fetchAssetSiteInventories = () => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getSiteInventories,
  data: { query: { pageSize: 1000 } },
  action: FETCH_ASSET_SITE_INVENTORIES,
});

// Loads a single AssetGroup with its deep graph (assetItems, childGroups,
// taskOccurrences, ...). The reducer merges the response into the currently
// selected sidebar item when the ids match, so the detail view has the deep
// relations that the flat-list endpoint doesn't return.
export const fetchAssetGroupDetail = (id) => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getAssetGroup,
  action: GET_ASSET_GROUP_DETAIL,
  data: { id },
  meta: { id },
});

export const fetchAssetItemDetail = (id) => ({
  type: API_REQUEST,
  callAPI: AssetsAPI.getAssetItem,
  action: GET_ASSET_ITEM_DETAIL,
  data: { id },
  meta: { id },
});

export const selectAssetNode = (nodeId) => ({
  type: SELECT_ASSET_NODE,
  nodeId,
});

export const toggleTreeNode = (nodeId) => ({
  type: TOGGLE_TREE_NODE,
  nodeId,
});

export const setAssetSearchTerm = (searchTerm) => ({
  type: SET_ASSET_SEARCH_TERM,
  searchTerm,
});

export const expandAllNodes = () => ({
  type: EXPAND_ALL_NODES,
});

export const collapseAllNodes = () => ({
  type: COLLAPSE_ALL_NODES,
});

export const openAddTemplateDrawer = () => ({
  type: OPEN_ADD_TEMPLATE_DRAWER,
});

export const closeAddTemplateDrawer = () => ({
  type: CLOSE_ADD_TEMPLATE_DRAWER,
});

export const deselectTemplate = () => ({
  type: DESELECT_TEMPLATE,
});

export const resetAssetView = () => ({
  type: RESET_ASSET_VIEW,
});

export const selectSidebarItem = (itemType, id, data) => ({
  type: SELECT_SIDEBAR_ITEM,
  itemType,
  id,
  data,
});

export const deselectSidebarItem = () => ({
  type: DESELECT_SIDEBAR_ITEM,
});

// Node Action Drawer
export const OPEN_NODE_ACTION_DRAWER = 'OPEN_NODE_ACTION_DRAWER';
export const CLOSE_NODE_ACTION_DRAWER = 'CLOSE_NODE_ACTION_DRAWER';

/**
 * Opens the node action drawer.
 * @param {string} mode - 'add' | 'addGroup' | 'addAsset' | 'editTask' | 'editTemplate' | 'editGroup' | 'editAsset'
 * @param {object} node - The node being acted on
 * @param {Array} breadcrumb - The breadcrumb path to the node
 */
export const openNodeActionDrawer = (
  mode,
  node,
  breadcrumb,
  initialItemType = null,
  editData = null
) => ({
  type: OPEN_NODE_ACTION_DRAWER,
  mode,
  node,
  breadcrumb,
  initialItemType,
  editData,
});

export const closeNodeActionDrawer = () => ({
  type: CLOSE_NODE_ACTION_DRAWER,
});
